import React, { useEffect, useRef } from "react";
import { motion, useAnimation } from "framer-motion";

interface CoolModeProps {
  children: React.ReactNode;
  options?: {
    particle?: string;
    particleCount?: number;
    particleSize?: number;
    duration?: number;
  };
}

export function CoolMode({ 
  children, 
  options = {
    particle: "https://pbs.twimg.com/profile_images/1782811051504885763/YR5-kWOI_400x400.jpg",
    particleCount: 10,
    particleSize: 20,
    duration: 1000
  }
}: CoolModeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const particles = useRef<HTMLDivElement[]>([]);
  const controls = useAnimation();

  const createParticle = (x: number, y: number) => {
    if (!containerRef.current) return;

    const particle = document.createElement("div");
    particle.style.position = "fixed";
    particle.style.pointerEvents = "none";
    particle.style.left = `${x}px`;
    particle.style.top = `${y}px`;
    particle.style.width = `${options.particleSize}px`;
    particle.style.height = `${options.particleSize}px`;
    particle.style.backgroundImage = `url(${options.particle})`;
    particle.style.backgroundSize = "cover";
    particle.style.borderRadius = "50%";
    particle.style.zIndex = "100";
    particle.style.transition = "transform 0.3s ease";

    containerRef.current.appendChild(particle);
    particles.current.push(particle);

    const angle = Math.random() * Math.PI * 2;
    const velocity = 2 + Math.random() * 2;
    const dx = Math.cos(angle) * velocity;
    const dy = Math.sin(angle) * velocity;
    let opacity = 1;
    let scale = 1;

    const animate = () => {
      if (!particle.parentNode) return;

      const x = parseFloat(particle.style.left);
      const y = parseFloat(particle.style.top);
      
      particle.style.left = `${x + dx}px`;
      particle.style.top = `${y + dy}px`;
      opacity -= 0.02;
      scale = Math.max(0.5, scale - 0.01);
      
      particle.style.opacity = opacity.toString();
      particle.style.transform = `scale(${scale}) rotate(${angle * 180 / Math.PI}deg)`;

      if (opacity > 0) {
        requestAnimationFrame(animate);
      } else {
        particle.remove();
        particles.current = particles.current.filter(p => p !== particle);
      }
    };

    requestAnimationFrame(animate);
  };

  const handleClick = (e: MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Create particles in a burst pattern
    for (let i = 0; i < (options.particleCount || 10); i++) {
      setTimeout(() => {
        createParticle(e.clientX, e.clientY);
      }, i * 50);
    }

    // Trigger button animation
    controls.start({
      scale: [1, 0.95, 1.05, 1],
      transition: { duration: 0.4 }
    });
  };

  useEffect(() => {
    const element = containerRef.current;
    if (!element) return;

    element.addEventListener("click", handleClick);
    return () => {
      element.removeEventListener("click", handleClick);
      particles.current.forEach(particle => particle.remove());
    };
  }, [options.particleCount]);

  return (
    <motion.div 
      ref={containerRef} 
      className="relative inline-block"
      animate={controls}
    >
      {children}
    </motion.div>
  );
}